// Pure-Redux hooks that replace the former Context-based useWorkState / useStateContext.
// No React Context here — just useSelector/useDispatch/useStore plus the module refs holder.
//
//   useWorkStateFields([...]) / useStateFields([...])  — subscribe (shallow-equal) ONLY to the
//     listed value fields, and also return the relevant setters (+ shared refs, for WorkState).
//   useWorkStateSetters() / useStateSetters()          — setters only, no state subscription.
import { useMemo } from 'react';
import { useSelector, useDispatch, useStore, shallowEqual } from 'react-redux';
import { SET_FIELD } from './index';
import { FIELD_TO_SLICE, setterName, WORKSTATE_SLICES, APP_LIFETIME_SLICES } from './fields';
import { workRefs } from './workRefs';

const WORKSTATE_FIELDS = Object.keys(FIELD_TO_SLICE).filter((f) => WORKSTATE_SLICES.includes(FIELD_TO_SLICE[f]));
const STATE_FIELDS = Object.keys(FIELD_TO_SLICE).filter((f) => APP_LIFETIME_SLICES.includes(FIELD_TO_SLICE[f]));

// Stable setters for a set of fields. dispatch + store are stable for the store's lifetime, so the
// built object can be memoized once (R2). The setX(prev => ...) updater form resolves against FRESH
// store state at call time (R1).
function buildSetters(dispatch, store, fields) {
  const obj = {};
  fields.forEach((field) => {
    const slice = FIELD_TO_SLICE[field];
    obj[setterName(field)] = (arg) => {
      const value = typeof arg === 'function' ? arg(store.getState()[slice][field]) : arg;
      dispatch(SET_FIELD[slice]({ key: field, value }));
    };
  });
  return obj;
}

function useFieldValues(fields) {
  return useSelector((s) => {
    const out = {};
    for (let i = 0; i < fields.length; i += 1) {
      const slice = FIELD_TO_SLICE[fields[i]];
      if (slice) out[fields[i]] = s[slice][fields[i]];
    }
    return out;
  }, shallowEqual);
}

export const useWorkStateSetters = () => {
  const dispatch = useDispatch();
  const store = useStore();
  return useMemo(() => buildSetters(dispatch, store, WORKSTATE_FIELDS), [dispatch, store]);
};

export const useWorkStateFields = (fields = []) => {
  const values = useFieldValues(fields);
  const setters = useWorkStateSetters();
  // workRefs is a stable singleton (identity never changes), so it needn't be a memo dep.
  return useMemo(() => ({ ...values, ...setters, ...workRefs }), [values, setters]);
};

export const useStateSetters = () => {
  const dispatch = useDispatch();
  const store = useStore();
  return useMemo(() => buildSetters(dispatch, store, STATE_FIELDS), [dispatch, store]);
};

export const useStateFields = (fields = []) => {
  const values = useFieldValues(fields);
  const setters = useStateSetters();
  return useMemo(() => ({ ...values, ...setters }), [values, setters]);
};
